import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, ExternalLink, Loader2, Play, Volume2, VolumeX, BookOpen, Headphones, Clapperboard, Film } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * FeaturedVideoStage - Phase 96 hero, Phase 108 multi-kind upgrade.
 *
 * Renders a 3D laptop/monitor frame with admin-curated MP4s autoplaying
 * inside. The pre-login visitor sees 4 pills (Flipbook / Audio book /
 * Video Book / Cinematic) right of the top corner - clicking a pill
 * swaps the video to that kind's currently-rotating showcase so
 * prospects immediately understand the four output formats Build Your
 * Book ships.
 *
 * Browser autoplay policies require the video start MUTED. We add a
 * Speaker / Mute icon button in the top-left of the video so the user
 * can opt in to audio with one tap (no global app-state change needed).
 */
const KIND_PILLS = [
  { key: 'cinematic', label: 'Cinematic', Icon: Clapperboard },
  { key: 'flipbook',  label: 'Flipbook',  Icon: BookOpen },
  { key: 'audiobook', label: 'Audio book', Icon: Headphones },
  { key: 'video_book', label: 'Video Book', Icon: Film },
];

function FeaturedVideoStage() {
  const [feature, setFeature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoFailed, setVideoFailed] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // Phase 108 - currently-selected pill (defaults to cinematic so we
  // hit the same admin doc the legacy `/daily-feature` endpoint used).
  const [currentKind, setCurrentKind] = useState('cinematic');
  // Phase 108 - availability map: { cinematic: true, flipbook: false, ... }
  // lets the pill UI show disabled state for kinds with no upload yet.
  const [available, setAvailable] = useState({});
  // Phase 108 - speaker icon state. Default muted (browser autoplay
  // policy); user opt-in unmutes via the icon button.
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  // Pre-fetch availability for ALL pills on mount so the UI can fade
  // out pills without uploaded videos. Parallel HEAD-ish JSON fetches;
  // no auth so this works for anonymous landing-page visitors too.
  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        const results = await Promise.all(
          KIND_PILLS.map((p) =>
            axios.get(`${API}/landing/videos/${p.key}`)
              .then((r) => [p.key, r.data])
              .catch(() => [p.key, { available: false }])
          )
        );
        if (cancelled) return;
        const map = {};
        let firstAvailable = null;
        for (const [key, data] of results) {
          map[key] = !!data?.available;
          if (data?.available && !firstAvailable) firstAvailable = { key, data };
        }
        setAvailable(map);
        if (firstAvailable) {
          setCurrentKind(firstAvailable.key);
          setFeature(firstAvailable.data);
        } else {
          // No pills have uploads - fall back to the legacy
          // daily-feature endpoint so we still show SOMETHING on
          // the hero (covers the production deployment that hasn't
          // re-uploaded the new kinds yet).
          const { data } = await axios.get(`${API}/landing/daily-feature`);
          if (!cancelled) setFeature(data);
        }
      } catch {
        if (!cancelled) setFeature({ available: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();
    return () => { cancelled = true; };
  }, []);

  // Phase 108 - when the user clicks a pill we refetch metadata for
  // that kind so the title / description below the screen updates
  // alongside the video src.
  useEffect(() => {
    if (loading) return;
    if (!available[currentKind]) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/landing/videos/${currentKind}`);
        if (!cancelled) {
          setFeature(data);
          setVideoFailed(false);
          setAutoplayBlocked(false);
        }
      } catch {
        if (!cancelled) setVideoFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [currentKind, available, loading]);

  // Programmatic autoplay - relying on the bare `autoPlay` attribute is
  // unreliable across Chrome / Safari / Firefox autoplay policies. We
  // explicitly call play() after metadata loads, and if the browser
  // blocks it (returns a rejected promise), we surface a tap-to-play
  // overlay instead of failing silently.
  //
  // Phase 100 - also seek past the static cover frame at the start of
  // the slideshow render (first ~1s is the cover) so the user always
  // sees motion immediately. Pure cinematic renders don't have a
  // cover frame, so seeking 0.6s into them is harmless.
  useEffect(() => {
    if (!feature || !feature.video_ready || videoFailed) return;
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      // Honour the user's current Speaker / Mute preference. Default
      // is muted (browser autoplay policy); the user can unmute via
      // the top-left toggle. We don't auto-reassert `muted = true`
      // because that would clobber a deliberate unmute.
      v.muted = muted;
      v.defaultMuted = muted;
      // Skip past the static cover frame on slideshow renders so the
      // user sees motion immediately. Guarded against NaN duration so
      // we never break short cinematic clips.
      if (feature.video_mode === 'full' && Number.isFinite(v.duration) && v.duration > 1.2) {
        try { v.currentTime = 0.7; } catch { /* harmless */ }
      }
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => setAutoplayBlocked(true));
      }
    };
    if (v.readyState >= 2) {
      tryPlay();
    } else {
      v.addEventListener('loadeddata', tryPlay, { once: true });
      return () => v.removeEventListener('loadeddata', tryPlay);
    }
  }, [feature, videoFailed]);

  // Track playback so we can surface a "▸ Playing" indicator pill —
  // makes it visually obvious the user is watching live motion instead
  // of a static cover image.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const on = () => setIsPlaying(true);
    const off = () => setIsPlaying(false);
    v.addEventListener('playing', on);
    v.addEventListener('pause', off);
    v.addEventListener('ended', off);
    return () => {
      v.removeEventListener('playing', on);
      v.removeEventListener('pause', off);
      v.removeEventListener('ended', off);
    };
  }, [feature, videoFailed]);

  // Phase 123 - when the current kind's MP4 fails to load (missing /
  // unreadable upload), fall back to the next available format pill;
  // if nothing else is uploaded, hide the laptop hero entirely.
  const handleVideoError = () => {
    const next = { ...available, [currentKind]: false };
    setAvailable(next);
    const fallback = KIND_PILLS.find((p) => next[p.key]);
    if (fallback) {
      setCurrentKind(fallback.key);
      setVideoFailed(false);
    } else {
      setVideoFailed(true);
      setFeature({ available: false });
    }
  };

  const handleManualPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().then(() => setAutoplayBlocked(false)).catch(() => {});
  };

  if (loading || !feature || !feature.available) {
    return null; // graceful no-op while marketplace seeds books
  }

  // Phase 108 - feature.video_url comes from either the new
  // `/landing/videos/{kind}` endpoint or the legacy
  // `/landing/daily-feature` (cinematic fallback). Either way it's
  // already prefixed with `/api/...` so BACKEND_URL + path resolves.
  const videoSrc = `${BACKEND_URL}${feature.video_url}`;
  // Cover image - only legacy `/daily-feature` includes a cover_url
  // (linked to a specific book_id). New kind endpoints don't have a
  // backdrop cover; show a soft brand gradient instead.
  const coverSrc = feature.cover_url ? `${BACKEND_URL}${feature.cover_url}` : null;
  // Phase 111 - admin-configurable share/CTA URL from the Video
  // Manager wins. Falls back to the legacy marketplace_url (if the
  // pick was tied to a specific book_id) and finally to /marketplace
  // so the CTA is always clickable.
  const ctaShareUrl = (feature.share_url || '').trim();
  const marketplaceTo = ctaShareUrl || feature.marketplace_url || '/marketplace';
  // External URLs (https://…) must use a real <a> with target=_blank;
  // internal paths (/book/abc) use React Router <Link>.
  const isExternalCta = /^https?:\/\//i.test(marketplaceTo);
  const showVideo = feature.video_ready && !videoFailed;
  const currentPill = KIND_PILLS.find((p) => p.key === currentKind) || KIND_PILLS[0];

  return (
    <div className="w-full flex justify-center mb-12" data-testid="featured-video-stage">
      <div className="w-full max-w-5xl">
        {/* Laptop body */}
        <div
          className="relative mx-auto rounded-[28px] sm:rounded-[36px] p-3 sm:p-5"
          style={{
            background:
              'linear-gradient(180deg, #1F2937 0%, #0F172A 50%, #020617 100%)',
            boxShadow:
              '0 30px 60px -20px rgba(2, 6, 23, 0.55), 0 8px 18px rgba(15, 23, 42, 0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          {/* Camera notch */}
          <div
            className="absolute left-1/2 -translate-x-1/2 top-1.5 sm:top-2.5 w-2 h-2 rounded-full"
            style={{
              background:
                'radial-gradient(circle at 30% 30%, #6B7280 0%, #111827 60%, #000 100%)',
            }}
            aria-hidden="true"
          />
          {/* Screen */}
          <div
            className="relative rounded-[16px] sm:rounded-[22px] overflow-hidden"
            style={{
              background: '#0B1220',
              aspectRatio: '16 / 10',
              boxShadow:
                'inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 30px 60px rgba(0,0,0,0.35)',
            }}
          >
            {/* Browser chrome */}
            <div
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5"
              style={{
                background: 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF5F57' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FEBC2E' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#28C840' }} />
              </span>
              <div
                className="ml-2 sm:ml-3 flex-1 flex items-center gap-2 rounded-full px-3 py-1 text-[10px] sm:text-xs font-medium"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  color: '#CBD5E1',
                  border: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <Lock size={10} style={{ color: '#94A3B8' }} />
                <span className="truncate">
                  buildyourbook.in
                </span>
              </div>
            </div>

            {/* Video viewport */}
            <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
              {/* Always-rendered cover backdrop - gives a polished poster
                  while the video loads, and stays visible as the fallback
                  if the MP4 fails to decode (e.g. legacy/missing video). */}
              {coverSrc ? (
                <img
                  src={coverSrc}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div
                  className="absolute inset-0"
                  aria-hidden="true"
                  style={{
                    background:
                      'linear-gradient(135deg, #1E293B 0%, #312E81 60%, #7C3AED 100%)',
                  }}
                />
              )}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(11,18,32,0.10) 0%, rgba(11,18,32,0.55) 70%, rgba(11,18,32,0.85) 100%)',
                }}
                aria-hidden="true"
              />
              {showVideo && (
                <video
                  ref={videoRef}
                  key={videoSrc}
                  src={videoSrc}
                  poster={coverSrc || undefined}
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  loop
                  muted={muted}
                  playsInline
                  preload="auto"
                  onError={handleVideoError}
                  data-testid="featured-video-stage-video"
                />
              )}

            {/* Phase 109 - Top-left stack: "Today's Featured Story"
                card on top, Speaker / Mute toggle below it. Stacked in
                a flex column so they share the left edge. */}
            <div
              className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col items-start gap-2 z-20"
              data-testid="featured-video-top-left-stack"
            >
              {(() => {
                // Phase 111 - when admin sets an external share_url
                // (e.g. social sharing destination, marketing page),
                // render a real <a target="_blank">. Internal app
                // paths still use React Router <Link>.
                const ctaProps = {
                  'data-testid': 'featured-video-stage-open-story',
                  className: 'flex items-center gap-2 sm:gap-3 rounded-full pl-3 sm:pl-4 pr-4 sm:pr-5 py-2 sm:py-2.5 transition-transform duration-300 hover:-translate-y-0.5 hover:scale-[1.03]',
                  style: {
                    background: 'rgba(15, 23, 42, 0.72)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
                    color: 'white',
                    maxWidth: '92%',
                  },
                };
                const ctaInner = (
                  <>
                    <span
                      className="inline-flex w-6 h-6 sm:w-7 sm:h-7 rounded-full items-center justify-center text-[11px] sm:text-xs font-bold"
                      style={{
                        background: 'linear-gradient(135deg, #FF8E71 0%, #F97316 100%)',
                        color: 'white',
                      }}
                      aria-hidden="true"
                    >
                      ▶
                    </span>
                    <span className="flex flex-col text-left min-w-0">
                      <span className="text-[10px] sm:text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#FDE68A' }}>
                        Today&apos;s Featured Story
                      </span>
                      <span className="text-xs sm:text-sm font-semibold truncate">
                        {feature.title}
                      </span>
                    </span>
                    <ExternalLink size={14} className="ml-1 shrink-0 opacity-80" />
                  </>
                );
                return isExternalCta ? (
                  <a href={marketplaceTo} target="_blank" rel="noreferrer noopener" {...ctaProps}>
                    {ctaInner}
                  </a>
                ) : (
                  <Link to={marketplaceTo} {...ctaProps}>
                    {ctaInner}
                  </Link>
                );
              })()}

              {/* Phase 108 - Speaker / Mute toggle. Default muted
                  (browser autoplay policy); tap unmutes to opt in
                  to audio. */}
              {showVideo && (
                <button
                  type="button"
                  onClick={() => {
                    const v = videoRef.current;
                    if (!v) return;
                    const next = !muted;
                    v.muted = next;
                    setMuted(next);
                    // Re-attempt play on unmute in case the user
                    // gesture also resolves an autoplay block.
                    v.play().catch(() => {});
                  }}
                  aria-label={muted ? 'Unmute video' : 'Mute video'}
                  data-testid="featured-video-mute-toggle"
                  className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-transform duration-200 hover:scale-110"
                  style={{
                    background: 'rgba(15, 23, 42, 0.72)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    color: '#FFFFFF',
                  }}
                >
                  {muted
                    ? <VolumeX size={16} />
                    : <Volume2 size={16} />
                  }
                </button>
              )}
            </div>

            {/* Phase 108 - kind pills overlay. Phase 109 moved them to
                the BOTTOM-RIGHT so the top-left now hosts the
                "Today's Featured Story" card + Mute toggle stack.
                Click switches the carousel to that kind's showcase.
                Every pill shares the same fixed width + chrome so the
                four sit as a clean stack regardless of which one is
                active or available. */}
              <div
                className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 flex flex-col items-end gap-1.5 z-20"
                data-testid="featured-video-kind-pills"
              >
                {KIND_PILLS.map((p) => {
                  const isCurrent = p.key === currentKind;
                  const isUp = !!available[p.key];
                  // Disabled appearance ONLY when the pill is not
                  // current AND has no upload yet. Current pill is
                  // always "alive".
                  const disabled = !isCurrent && !isUp;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => !disabled && !isCurrent && setCurrentKind(p.key)}
                      disabled={disabled || isCurrent}
                      aria-label={isCurrent ? `${p.label} demo playing` : `Show ${p.label} demo`}
                      data-testid={
                        isCurrent
                          ? 'featured-video-stage-playing-pill'
                          : `featured-video-pill-${p.key}`
                      }
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-all duration-200 hover:scale-[1.04]"
                      style={{
                        // Fixed uniform width so the four pills stack
                        // as a clean column regardless of label length.
                        width: '120px',
                        justifyContent: 'flex-start',
                        background: isCurrent
                          ? 'rgba(15, 23, 42, 0.82)'
                          : 'rgba(15, 23, 42, 0.62)',
                        color: isCurrent
                          ? '#FFFFFF'
                          : disabled ? '#94A3B8' : '#E5E7EB',
                        backdropFilter: 'blur(8px)',
                        border: isCurrent
                          ? '1px solid rgba(34,197,94,0.45)'
                          : '1px solid rgba(255,255,255,0.18)',
                        cursor: isCurrent ? 'default' : disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.55 : 1,
                      }}
                    >
                      {isCurrent ? (
                        <span
                          className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0"
                          style={{ background: '#22C55E' }}
                          aria-hidden="true"
                        />
                      ) : (
                        <p.Icon size={12} aria-hidden="true" className="shrink-0" />
                      )}
                      <span className="truncate">{p.label}</span>
                    </button>
                  );
                })}
              </div>
              {/* Playing indicator now rendered as part of the
                  kind-pills overlay above (the highlighted pill
                  shows live status). */}
              {/* Tap-to-play overlay surfaces when the browser blocks
                  autoplay (most often Safari + Chrome's strict policy
                  on first-page-load without prior user interaction). */}
              {showVideo && autoplayBlocked && (
                <button
                  type="button"
                  onClick={handleManualPlay}
                  aria-label="Play featured video"
                  data-testid="featured-video-stage-play-overlay"
                  className="absolute inset-0 flex items-center justify-center group"
                  style={{ background: 'rgba(11, 18, 32, 0.18)' }}
                >
                  <span
                    className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full transition-transform duration-300 group-hover:scale-110"
                    style={{
                      background: 'rgba(255,255,255,0.92)',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
                    }}
                  >
                    <Play size={28} style={{ color: '#1F2937', marginLeft: '4px' }} fill="#1F2937" />
                  </span>
                </button>
              )}
              {!feature.video_ready && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <Loader2 size={28} className="animate-spin" style={{ color: '#FDE68A' }} />
                  <div className="text-sm sm:text-base font-medium text-white/85" style={{ fontFamily: 'Fredoka' }}>
                    Generating today&apos;s cinematic feature&hellip;
                  </div>
                  <div className="text-xs text-white/55">
                    Fresh Fal.ai render - ready in ~60–120s.
                  </div>
                </div>
              )}

              {/* Phase 109 - bottom-center CTA removed; the
                  "Today's Featured Story" card now lives in the
                  top-left stack alongside the Mute toggle. */}
            </div>
          </div>
        </div>

        {/* Monitor stand */}
        <div className="flex flex-col items-center" aria-hidden="true">
          <div
            className="w-20 sm:w-28 h-3 sm:h-4"
            style={{
              background: 'linear-gradient(180deg, #1F2937 0%, #0F172A 100%)',
              clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0 100%)',
            }}
          />
          <div
            className="w-32 sm:w-48 h-2 rounded-b-2xl"
            style={{
              background: 'linear-gradient(180deg, #0F172A 0%, #020617 100%)',
              boxShadow: '0 10px 24px rgba(2, 6, 23, 0.35)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default FeaturedVideoStage;
